import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  SeahorseTemplateDirective,
  ShDatePickerComponent,
  ShDrawerComponent,
  ShFieldComponent,
  ShInputComponent,
  ShMultiselectComponent,
  ShPopoverComponent,
  ShSearchInputComponent,
  ShSelectComponent,
  ShToggleComponent,
} from '@seahorse/components';
import type { ActionMenuItem } from '@seahorse/ui';
import { ActionBarComponent } from '../action-bar/action-bar.component';
import {
  formatFilterValue,
  isFilterActive,
  PageAction,
  PageFilter,
  PageFilterValues,
  PageView,
} from './page-header.models';

/** Content equality for filter lists: same keys, types, labels, options and values. */
export function sameFilters(a: PageFilter[], b: PageFilter[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((f, i) => {
    const g = b[i];
    return (
      f.key === g.key &&
      f.type === g.type &&
      f.label === g.label &&
      JSON.stringify(f.options ?? null) === JSON.stringify(g.options ?? null) &&
      JSON.stringify(f.value ?? null) === JSON.stringify(g.value ?? null)
    );
  });
}

/** Filter value equality; all "empty" values (undefined, null, '', []) count as equal. */
export function sameFilterValue(a: unknown, b: unknown): boolean {
  const isEmpty = (v: unknown) =>
    v === undefined ||
    v === null ||
    v === '' ||
    (Array.isArray(v) && v.length === 0);
  if (isEmpty(a) && isEmpty(b)) return true;
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

const SEARCH_DEBOUNCE_MS = 300;
const TEXT_FILTER_DEBOUNCE_MS = 300;

/**
 * The two-tier, data-driven page header (design handoff, "Step 4,
 * streamlined: the page header"). Pages pass views, actions and filters as
 * data; the header never contains page-specific buttons or controls. Both
 * tiers render only when they have content: tier 1 needs views or actions,
 * tier 2 needs `showSearch` or filters.
 *
 * The header keeps a local working copy of the filter values (`_filters`),
 * seeded from the `filters` input and re-seeded whenever that input
 * changes, so a page that restores values from the URL is reflected. The
 * page owns the real state: every change is emitted through
 * `filtersChange` with the whole value set, immediately, with no Apply
 * button (text is debounced).
 */
@Component({
  selector: 'sh-page-header',
  templateUrl: './page-header.component.html',
  // Default, not OnPush: its `sh-action-bar` evaluates action
  // `visible`/`disabled` functions over page state; an OnPush host with
  // unchanged inputs would skip that subtree and leave them stale.
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    TranslateModule,
    SeahorseTemplateDirective,
    ShPopoverComponent,
    ShFieldComponent,
    ShSelectComponent,
    ShMultiselectComponent,
    ShInputComponent,
    ShSearchInputComponent,
    ShToggleComponent,
    ShDatePickerComponent,
    ShDrawerComponent,
    ActionBarComponent,
  ],
})
export class PageHeaderComponent {
  views = input<PageView[]>();
  activeView = input<string>();

  primaryAction = input<PageAction>();
  secondaryActions = input<PageAction[]>();
  overflowActions = input<ActionMenuItem[]>();

  showSearch = input(false);
  search = input('');
  searchPlaceholder = input<string>();

  filters = input<PageFilter[]>();

  resultCount = input<number | null>();
  totalCount = input<number | null>();

  readonly viewChange = output<string>();
  readonly searchChange = output<string>();
  readonly filtersChange = output<PageFilterValues>();

  protected readonly translate = inject(TranslateService);

  // Local working copies -- see the class doc comment.
  protected readonly searchText = signal('');
  protected readonly workingFilters = signal<PageFilter[]>([]);

  // Which filter's control the shared popover currently shows. `undefined`
  // when the popover is closed (or showing the "+ Add filter" list, which
  // has its own popover).
  protected readonly activeFilterKey = signal<string | undefined>(undefined);

  // A filter just picked from "+ Add filter": it shows as a filter link right
  // away (still without a value), and its popover opens on that link. It
  // stays a link only while the popover is open, unless it got a value.
  protected readonly pendingFilterKey = signal<string | undefined>(undefined);

  // Bound to the drawer's [(visible)].
  protected readonly drawerOpen = signal(false);

  // True when tier 2 has wrapped and the filters start a new line below the
  // search box: the divider between them then trails the search line, so
  // it's hidden (visibility only, so hiding it can't change the wrap).
  protected readonly filtersWrapped = signal(false);

  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _injector = inject(Injector);

  protected addFilterList: PageFilter[] = [];

  @ViewChild('filterPopover') private _filterPopover?: ShPopoverComponent;
  @ViewChild('addFilterPopover') private _addFilterPopover?: ShPopoverComponent;

  private _searchDebounce?: ReturnType<typeof setTimeout>;
  private _textFilterDebounce?: ReturnType<typeof setTimeout>;

  constructor() {
    effect(
      () => {
        this.searchText.set(this.search());
      },
      { allowSignalWrites: true }
    );
    effect(
      () => {
        // Deep-clone each filter so edits to the working copy (e.g. an
        // in-progress multiselect selection) never mutate the page's own
        // filter objects before they're emitted.
        this.workingFilters.set(this._stableFilters().map((f) => ({ ...f })));
      },
      { allowSignalWrites: true }
    );

    // CSS can't tell whether a flex row has wrapped, so measure it whenever
    // the header's size changes (a wrap changes its height).
    afterNextRender(() => {
      if (typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(() => this._checkFiltersWrapped());
      observer.observe(this._host.nativeElement);
      this._destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  private _checkFiltersWrapped(): void {
    const host = this._host.nativeElement;
    const search = host.querySelector('.page-search');
    const filters = host.querySelector('.page-filters');
    const wrapped =
      !!search &&
      !!filters &&
      filters.getBoundingClientRect().top >
        search.getBoundingClientRect().bottom - 1;
    this.filtersWrapped.set(wrapped);
  }

  // The `filters` input compared by content, not by reference. A page that
  // builds its filter list in a getter (or a config-driven page that maps
  // it on the fly) hands over a new array on every change detection; without
  // this, each one re-seeds the working copy, which schedules another change
  // detection, and the page never settles (it froze invoice executions).
  private readonly _stableFilters = computed(() => this.filters() ?? [], {
    equal: sameFilters,
  });

  // --- Tier visibility -----------------------------------------------

  protected hasTier1(): boolean {
    return (
      (this.views()?.length ?? 0) > 0 ||
      !!this.primaryAction() ||
      (this.secondaryActions()?.length ?? 0) > 0 ||
      (this.overflowActions()?.length ?? 0) > 0
    );
  }

  protected hasTier2(): boolean {
    return this.showSearch() || (this.filters()?.length ?? 0) > 0;
  }

  protected hasOverflow(): boolean {
    return (this.overflowActions()?.length ?? 0) > 0;
  }

  // --- Views -----------------------------------------------------------

  protected selectView(view: PageView): void {
    this.viewChange.emit(view.key);
  }

  // --- Search --------------------------------------------------------

  protected onSearchInput(value: string): void {
    this.searchText.set(value);
    if (this._searchDebounce) clearTimeout(this._searchDebounce);
    this._searchDebounce = setTimeout(() => {
      this.searchChange.emit(value);
    }, SEARCH_DEBOUNCE_MS);
  }

  // --- Filters: reading -----------------------------------------------

  protected activeFilters(): PageFilter[] {
    return this.workingFilters().filter(isFilterActive);
  }

  // The filter links: the active filters plus the one just picked from
  // "+ Add filter", in the page's own filter order.
  protected linkFilters(): PageFilter[] {
    const pending = this.pendingFilterKey();
    return this.workingFilters().filter(
      (f) => isFilterActive(f) || f.key === pending
    );
  }

  protected inactiveFilters(): PageFilter[] {
    return this.workingFilters().filter((f) => !isFilterActive(f));
  }

  protected hasActiveFilters(): boolean {
    return this.activeFilters().length > 0;
  }

  protected hasInactiveFilters(): boolean {
    return this.inactiveFilters().length > 0;
  }

  protected filterValueText(filter: PageFilter): string {
    return formatFilterValue(filter, this.translate.currentLang, {
      yes: this.translate.instant('shared.terms.yes'),
      no: this.translate.instant('shared.terms.no'),
    });
  }

  protected activeFilter(): PageFilter | undefined {
    const key = this.activeFilterKey();
    return key === undefined
      ? undefined
      : this.workingFilters().find((f) => f.key === key);
  }

  // --- Filters: opening popovers ---------------------------------------

  protected openFilterPopover(event: Event, filter: PageFilter): void {
    event.stopPropagation();
    if (this.pendingFilterKey() !== filter.key) {
      this.pendingFilterKey.set(undefined);
    }
    this.activeFilterKey.set(filter.key);
    this._filterPopover?.show(event, event.currentTarget);
  }

  protected openAddFilterList(event: Event): void {
    event.stopPropagation();
    this.addFilterList = this.inactiveFilters();
    this._addFilterPopover?.show(event, event.currentTarget);
  }

  // The picked filter becomes a link straight away and its popover opens on
  // that link, so the list can close: anchoring the popover to the list item
  // left it floating once the list was gone.
  protected pickAddFilter(event: Event, filter: PageFilter): void {
    event.stopPropagation();
    this._addFilterPopover?.hide();
    this.pendingFilterKey.set(filter.key);
    this.activeFilterKey.set(filter.key);
    afterNextRender(
      () => {
        const link = this._host.nativeElement.querySelector(
          `.filter-link[data-filter-key="${filter.key}"]`
        );
        if (link) this._filterPopover?.show(event, link);
      },
      { injector: this._injector }
    );
  }

  // A picked filter left without a value drops out of the links again.
  protected onFilterPopoverClosed(): void {
    this.pendingFilterKey.set(undefined);
  }

  protected openFiltersDrawer(): void {
    this.drawerOpen.set(true);
  }

  protected closeFiltersDrawer(): void {
    this.drawerOpen.set(false);
  }

  // --- Filters: writing --------------------------------------------------

  protected setFilterValue(filter: PageFilter, value: unknown): void {
    if (!this._updateFilter(filter.key, value)) return;
    this._emitFilters();
  }

  // The filter-link popover closes once a choice is complete: after picking
  // a single select value, a yes/no or a date, and after the second date of
  // a range. Multiselect and text stay open, since you usually pick several
  // values or keep typing. (The "All filters" drawer uses setFilterValue and
  // stays open.)
  protected setPopoverFilterValue(filter: PageFilter, value: unknown): void {
    this.setFilterValue(filter, value);
    const rangeComplete =
      filter.type === 'dateRange' &&
      Array.isArray(value) &&
      value.length === 2 &&
      value[0] != null &&
      value[1] != null;
    if (
      filter.type === 'select' ||
      filter.type === 'boolean' ||
      filter.type === 'date' ||
      rangeComplete
    ) {
      this._filterPopover?.hide();
    }
  }

  protected onTextFilterInput(filter: PageFilter, value: unknown): void {
    if (!this._updateFilter(filter.key, value)) return;
    if (this._textFilterDebounce) clearTimeout(this._textFilterDebounce);
    this._textFilterDebounce = setTimeout(
      () => this._emitFilters(),
      TEXT_FILTER_DEBOUNCE_MS
    );
  }

  protected clearFilter(filter: PageFilter): void {
    this._updateFilter(filter.key, undefined);
    this._filterPopover?.hide();
    this._emitFilters();
  }

  protected clearAllFilters(): void {
    this.workingFilters.set(
      this.workingFilters().map((f) => ({ ...f, value: undefined }))
    );
    this._emitFilters();
  }

  // Returns false (and changes nothing) when the value didn't really change.
  // PrimeNG controls can emit a model change while initialising (e.g. null
  // -> undefined); treating that as a change replaced the filter object,
  // *ngFor recreated the control, which emitted again: an endless loop that
  // froze every page with filters.
  private _updateFilter(key: string, value: unknown): boolean {
    const current = this.workingFilters().find((f) => f.key === key);
    if (!current || sameFilterValue(current.value, value)) return false;
    this.workingFilters.set(
      this.workingFilters().map((f) => (f.key === key ? { ...f, value } : f))
    );
    return true;
  }

  protected readonly trackByKey = (_: number, item: { key: string }) =>
    item.key;

  private _emitFilters(): void {
    const values: PageFilterValues = {};
    for (const filter of this.workingFilters()) {
      values[filter.key] = filter.value;
    }
    this.filtersChange.emit(values);
  }
}
